/**
 * 本地收藏（纯前端 localStorage，无后端、无账号体系）
 * - key: aid-favorites
 * - 数据结构: [{ push_date, title, topic_type, time }]
 * - 跨设备迁移: 导出/导入 JSON 文件
 * - 用 Vue reactive 做跨组件响应式（详情页收藏 → 列表页筛选即时刷新）
 */
import { reactive } from 'vue'

const KEY = 'aid-favorites'

function load () {
  try {
    const raw = localStorage.getItem(KEY)
    const arr = raw ? JSON.parse(raw) : []
    return Array.isArray(arr) ? arr.filter(it => it && it.push_date) : []
  } catch (e) {
    return []
  }
}

// 全局响应式状态（import 同一模块共享）
export const favState = reactive({ items: load() })

function persist () {
  localStorage.setItem(KEY, JSON.stringify(favState.items))
}

/** 是否已收藏 */
export function has (pushDate) {
  return favState.items.some(it => it.push_date === pushDate)
}

/**
 * 切换收藏状态
 * @param item { push_date, title, topic_type }
 * @returns true=收藏后已收藏，false=已取消
 */
export function toggle (item) {
  if (!item || !item.push_date) return false
  const idx = favState.items.findIndex(it => it.push_date === item.push_date)
  if (idx >= 0) {
    favState.items.splice(idx, 1)
    persist()
    return false
  }
  favState.items.unshift({
    push_date: item.push_date,
    title: item.title || '',
    topic_type: item.topic_type || '',
    time: Date.now()
  })
  persist()
  return true
}

/** 收藏日期集合（列表筛选用） */
export function dateSet () {
  return new Set(favState.items.map(it => it.push_date))
}

/** 导出为 JSON 文件下载 */
export function exportJson () {
  const blob = new Blob([JSON.stringify(favState.items, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'aid-favorites.json'
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * 导入 JSON 文件（合并，按 push_date 去重，已存在的保留时间较新的一条）
 * @param file File 对象
 * @returns Promise<number> 新导入的条数
 */
export function importJson (file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('文件读取失败'))
    reader.onload = () => {
      try {
        const arr = JSON.parse(reader.result)
        if (!Array.isArray(arr)) throw new Error('格式错误：应为数组')
        const valid = arr.filter(it => it && typeof it.push_date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(it.push_date))
        const map = new Map(favState.items.map(it => [it.push_date, it]))
        let added = 0
        for (const it of valid) {
          const exist = map.get(it.push_date)
          if (!exist) {
            added++
            map.set(it.push_date, {
              push_date: it.push_date,
              title: String(it.title || ''),
              topic_type: String(it.topic_type || ''),
              time: Number(it.time) || Date.now()
            })
          } else if ((Number(it.time) || 0) > (exist.time || 0)) {
            exist.title = String(it.title || exist.title)
            exist.topic_type = String(it.topic_type || exist.topic_type)
            exist.time = Number(it.time)
          }
        }
        // 按收藏时间倒序写回
        favState.items = [...map.values()].sort((a, b) => (b.time || 0) - (a.time || 0))
        persist()
        resolve(added)
      } catch (e) {
        reject(e instanceof Error ? e : new Error('JSON 解析失败'))
      }
    }
    reader.readAsText(file)
  })
}

export default { favState, has, toggle, dateSet, exportJson, importJson }

/* ================= 精选收藏（AI 每日精选，以 url 为唯一标识） ================= */

const SEL_KEY = 'aid-fav-selected'

/** 精选收藏全局状态: [{ url, title, subtitle, source, heat, sel_date, time }] */
export const selFavState = reactive({ items: loadKey(SEL_KEY) })

function loadKey (key) {
  try {
    const raw = localStorage.getItem(key)
    const arr = raw ? JSON.parse(raw) : []
    return Array.isArray(arr) ? arr.filter(it => it && it.url) : []
  } catch (e) {
    return []
  }
}

function persistSel () {
  localStorage.setItem(SEL_KEY, JSON.stringify(selFavState.items))
}

/** 精选条目是否已收藏（按 url 判重） */
export function selHas (url) {
  return !!url && selFavState.items.some(it => it.url === url)
}

/**
 * 切换精选收藏
 * @param item 精选条目 { url, title, subtitle, source, heat }
 * @param selDate 所属精选日期 YYYY-MM-DD
 * @returns true=收藏后已收藏，false=已取消
 */
export function selToggle (item, selDate) {
  if (!item || !item.url) return false
  const idx = selFavState.items.findIndex(it => it.url === item.url)
  if (idx >= 0) {
    selFavState.items.splice(idx, 1)
    persistSel()
    return false
  }
  selFavState.items.unshift({
    url: item.url,
    title: item.title || '',
    subtitle: item.subtitle || '',
    source: item.source || '',
    heat: item.heat || '',
    sel_date: selDate || '',
    time: Date.now()
  })
  persistSel()
  return true
}

/** 已收藏精选的 url 集合（列表筛选用） */
export function selUrlSet () {
  return new Set(selFavState.items.map(it => it.url))
}

/** 导出精选收藏为 JSON 文件 */
export function exportSelJson () {
  const blob = new Blob([JSON.stringify(selFavState.items, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'aid-favorites-selected.json'
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * 导入精选收藏 JSON（按 url 去重合并，时间较新者胜出）
 * @returns Promise<number> 新导入条数
 */
export function importSelJson (file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('文件读取失败'))
    reader.onload = () => {
      try {
        const arr = JSON.parse(reader.result)
        if (!Array.isArray(arr)) throw new Error('格式错误：应为数组')
        const valid = arr.filter(it => it && typeof it.url === 'string' && /^https?:\/\//.test(it.url))
        const map = new Map(selFavState.items.map(it => [it.url, it]))
        let added = 0
        for (const it of valid) {
          const exist = map.get(it.url)
          const rec = {
            url: it.url,
            title: String(it.title || ''),
            subtitle: String(it.subtitle || ''),
            source: String(it.source || ''),
            heat: String(it.heat || ''),
            sel_date: String(it.sel_date || ''),
            time: Number(it.time) || Date.now()
          }
          if (!exist) {
            added++
            map.set(it.url, rec)
          } else if (rec.time > (exist.time || 0)) {
            map.set(it.url, rec)
          }
        }
        selFavState.items = [...map.values()].sort((a, b) => (b.time || 0) - (a.time || 0))
        persistSel()
        resolve(added)
      } catch (e) {
        reject(e instanceof Error ? e : new Error('JSON 解析失败'))
      }
    }
    reader.readAsText(file)
  })
}
